import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Plus, Search, X } from 'lucide-react'
import { Card, CardContent } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Skeleton } from '../../components/ui/Skeleton'
import { EmptyState } from '../../components/EmptyState'
import PullToRefresh from '../../components/PullToRefresh'
import { useExercises } from '../../hooks/useExercises'
import { performRefresh } from '../../lib/refresh'
import { cn } from '../../lib/utils'
import WorkoutBackHeader from './components/WorkoutBackHeader'
import ExerciseCard from './components/ExerciseCard'
import ExerciseForm from './components/ExerciseForm'

const FILTER_GROUPS = ['all', 'chest', 'back', 'shoulders', 'biceps', 'triceps', 'quads', 'hamstrings', 'core']

export default function ExerciseLibrary() {
  const { t } = useTranslation()
  const { exercises, loading, createExercise, refetch } = useExercises()
  const handleRefresh = useCallback(() => performRefresh([refetch]), [refetch])
  const [filter, setFilter] = useState('all')
  const [search, setSearch] = useState('')
  const [creating, setCreating] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [newId] = useState(() => crypto.randomUUID())

  const filtered = useMemo(() => {
    return exercises.filter((e) => {
      if (filter !== 'all' && e.muscle_group !== filter) {
        // Allow "quads" filter to also match quads/hamstrings/glutes
        if (filter === 'quads' && ['quads', 'hamstrings', 'glutes'].includes(e.muscle_group)) {
          // pass
        } else {
          return false
        }
      }
      if (search && !e.name.toLowerCase().includes(search.toLowerCase())) return false
      return true
    })
  }, [exercises, filter, search])

  const handleCreate = async (values) => {
    setSubmitting(true)
    const created = await createExercise(values)
    setSubmitting(false)
    if (created) setCreating(false)
  }

  return (
    <PullToRefresh onRefresh={handleRefresh}>
    <div className="space-y-4 content-fade">
      <WorkoutBackHeader
        title={t('workout.exercises.title')}
        subtitle={t('workout.exercises.subtitle')}
        action={
          !creating && (
            <Button
              onClick={() => setCreating(true)}
              size="sm"
              className="bg-app-primary-500 hover:bg-app-primary-600 text-white"
            >
              <Plus className="w-4 h-4 me-1" />
              {t('workout.exercises.addNew')}
            </Button>
          )
        }
      />

      {creating && (
        <Card>
          <CardContent className="p-4 sm:p-6">
            <ExerciseForm
              idForUpload={newId}
              onSubmit={handleCreate}
              onCancel={() => setCreating(false)}
              submitting={submitting}
            />
          </CardContent>
        </Card>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t('workout.exercises.searchPlaceholder')}
          className="h-11 w-full ps-10 pe-9 rounded-lg border border-input bg-background text-foreground text-base sm:text-sm focus:outline-none"
        />
        {search && (
          <button
            onClick={() => setSearch('')}
            className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Filter chips */}
      <div className="flex gap-1.5 overflow-x-auto -mx-1 px-1 pb-1">
        {FILTER_GROUPS.map((g) => (
          <button
            key={g}
            onClick={() => setFilter(g)}
            className={cn(
              'h-8 px-3 rounded-full text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0',
              filter === g
                ? 'bg-app-primary-500 text-white'
                : 'bg-muted text-muted-foreground hover:bg-accent'
            )}
          >
            {g === 'all' ? t('workout.exercises.filterAll') : t(`workout.muscleGroup.${g}`)}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="aspect-[3/4] w-full" />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={Search}
          title={t('workout.exercises.noResults')}
        />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((ex) => (
            <ExerciseCard key={ex.id} exercise={ex} />
          ))}
        </div>
      )}
    </div>
    </PullToRefresh>
  )
}
